import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { defaults as defaultInteractions } from "ol/interaction";
import { equals } from "ol/extent";
import OLMap from "ol/Map";
import OLCollection from "ol/Collection";
import View from "ol/View";
import { unByKey } from "ol/Observable";
import Interaction from "ol/interaction/Interaction";
import { Layer } from "mobility-toolbox-js/ol";
import ResizeHandler from "../ResizeHandler";
const propTypes = {
  animationOptions: PropTypes.shape({
    center: PropTypes.arrayOf(PropTypes.number),
    resolution: PropTypes.number,
    zoom: PropTypes.number
  }),
  center: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
  extent: PropTypes.arrayOf(PropTypes.number),
  fitOptions: PropTypes.object,
  interactions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.instanceOf(Interaction)),
    PropTypes.instanceOf(OLCollection)
  ]),
  layers: PropTypes.arrayOf(PropTypes.instanceOf(Layer)),
  map: PropTypes.instanceOf(OLMap),
  onFeaturesClick: PropTypes.func,
  featuresClickOptions: PropTypes.shape({
    layerFilter: PropTypes.func,
    hitTolerance: PropTypes.number,
    checkWrapped: PropTypes.bool
  }),
  onFeaturesHover: PropTypes.func,
  onMapMoved: PropTypes.func,
  resolution: PropTypes.number,
  tabIndex: PropTypes.number,
  style: PropTypes.object,
  ariaLabel: PropTypes.string,
  viewOptions: PropTypes.shape({
    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,
    extent: PropTypes.array,
    projection: PropTypes.string
  }),
  zoom: PropTypes.number
};
const defaultProps = {
  animationOptions: void 0,
  center: [0, 0],
  className: "rs-map",
  extent: void 0,
  fitOptions: {
    duration: 1e3,
    padding: [20, 20, 20, 20],
    maxZoom: 23
  },
  style: void 0,
  interactions: null,
  layers: [],
  map: null,
  onFeaturesClick: void 0,
  featuresClickOptions: {
    hitTolerance: 0
  },
  onFeaturesHover: void 0,
  onMapMoved: void 0,
  resolution: void 0,
  tabIndex: void 0,
  ariaLabel: "map",
  viewOptions: {
    minZoom: 0,
    maxZoom: 22,
    extent: void 0,
    projection: "EPSG:3857"
  },
  zoom: 1
};
class BasicMap extends PureComponent {
  constructor(props) {
    super(props);
    const { map, interactions } = this.props;
    this.map = map || new OLMap({
      controls: [],
      interactions: interactions || defaultInteractions({
        altShiftDragRotate: false,
        pinchRotate: false
      })
    });
    this.state = {
      node: null
    };
    this.layers = [];
    this.moveEndRef = null;
    this.singleClickRef = null;
    this.pointerMoveRef = null;
    this.setNode = this.setNode.bind(this);
  }
  componentDidMount() {
    const { layers, extent, viewOptions, center, zoom, resolution } = this.props;
    const { node } = this.state;
    this.map.setTarget(node);
    this.map.setView(new View({ ...viewOptions, center, zoom, resolution }));
    const viewPort = this.map.getViewport();
    viewPort.style.touchAction = "none";
    viewPort.style.msTouchAction = "none";
    viewPort.setAttribute("touch-action", "none");
    if (this.map.getSize() && extent) {
      this.map.getView().fit(extent);
    }
    this.setLayers(layers);
    this.listenMoveEnd();
    this.listenSingleClick();
    this.listenPointerMove();
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      animationOptions,
      center,
      extent,
      fitOptions,
      layers,
      resolution,
      viewOptions,
      zoom,
      onMapMoved,
      onFeaturesClick,
      onFeaturesHover
    } = this.props;
    const { node } = this.state;
    if (prevState.node !== node) {
      this.map.setTarget(node);
      if (!prevState.node && node && extent) {
        this.map.getView().fit(extent);
      }
    }
    if (prevProps.layers !== layers) {
      this.setLayers(layers, prevProps.layers);
    }
    if (viewOptions && JSON.stringify(viewOptions) !== JSON.stringify(prevProps.viewOptions)) {
      this.map.setView(
        new View({
          ...viewOptions,
          center,
          resolution,
          zoom
        })
      );
    }
    const view = this.map.getView();
    if (animationOptions && prevProps.animationOptions !== animationOptions) {
      view.animate(animationOptions);
    }
    if (prevProps.center !== center) {
      view.setCenter(center);
    }
    if (zoom !== prevProps.zoom) {
      view.setZoom(zoom);
    }
    if (resolution !== prevProps.resolution) {
      view.setResolution(resolution);
    }
    if (extent && !equals(extent, prevProps.extent || [])) {
      view.fit(extent, fitOptions);
    }
    if (onMapMoved !== prevProps.onMapMoved) {
      this.listenMoveEnd();
    }
    if (onFeaturesClick !== prevProps.onFeaturesClick) {
      this.listenSingleClick();
    }
    if (onFeaturesHover !== prevProps.onFeaturesHover) {
      this.listenPointerMove();
    }
  }
  componentWillUnmount() {
    unByKey([this.moveEndRef, this.singleClickRef, this.pointerMoveRef]);
  }
  setNode(node) {
    this.setState({ node });
  }
  setLayers(layers = [], prevLayers = []) {
    for (let i = 0; i < prevLayers.length; i += 1) {
      this.terminateLayer(prevLayers[i]);
    }
    for (let i = 0; i < layers.length; i += 1) {
      this.initLayer(layers[i]);
    }
    this.layers = layers;
  }
  initLayer(layer) {
    if (layer.attachToMap) {
      layer.attachToMap(this.map);
    }
    if (layer.init) {
      layer.init(this.map);
    }
    if (layer.olLayer && this.map.getLayers() && !this.map.getLayers().getArray().includes(layer.olLayer)) {
      this.map.addLayer(layer.olLayer);
    }
    const layers = layer.children || [];
    for (let i = 0; i < layers.length; i += 1) {
      this.initLayer(layers[i]);
    }
  }
  terminateLayer(layer) {
    const layers = layer.children || [];
    for (let i = 0; i < layers.length; i += 1) {
      this.terminateLayer(layers[i]);
    }
    if (layer.olLayer && this.map.getLayers() && this.map.getLayers().getArray().includes(layer.olLayer)) {
      this.map.removeLayer(layer.olLayer);
    }
    if (layer.terminate) {
      layer.terminate(this.map);
    }
    if (layer.detachFromMap) {
      layer.detachFromMap(this.map);
    }
  }
  listenMoveEnd() {
    const { onMapMoved } = this.props;
    unByKey(this.moveEndRef);
    if (!onMapMoved) {
      return;
    }
    this.moveEndRef = this.map.on("moveend", (evt) => {
      return onMapMoved(evt);
    });
  }
  listenSingleClick() {
    const { onFeaturesClick, featuresClickOptions } = this.props;
    unByKey(this.singleClickRef);
    if (!onFeaturesClick) {
      return;
    }
    this.singleClickRef = this.map.on("singleclick", (evt) => {
      const features = evt.map.getFeaturesAtPixel(
        evt.pixel,
        featuresClickOptions
      );
      onFeaturesClick(features || [], evt);
    });
  }
  listenPointerMove() {
    const { onFeaturesHover } = this.props;
    unByKey(this.pointerMoveRef);
    if (!onFeaturesHover) {
      return;
    }
    this.pointerMoveRef = this.map.on("pointermove", (evt) => {
      const features = evt.map.getFeaturesAtPixel(evt.pixel);
      onFeaturesHover(features || [], evt);
    });
  }
  render() {
    const { className, tabIndex, ariaLabel, style } = this.props;
    const { node } = this.state;
    return /* @__PURE__ */ React.createElement("div", {
      className,
      ref: this.setNode,
      role: "presentation",
      "aria-label": ariaLabel,
      tabIndex,
      style
    }, /* @__PURE__ */ React.createElement(ResizeHandler, {
      maxHeightBrkpts: null,
      maxWidthBrkpts: null,
      observe: node,
      onResize: () => {
        this.map.updateSize();
      }
    }));
  }
}
BasicMap.propTypes = propTypes;
BasicMap.defaultProps = defaultProps;
export default BasicMap;
